import styled from 'styled-components';
import { colors, Content } from '../../styles/common';

type CaseStudyContentProps = { isTest?: boolean };

export const CaseStudyContent = styled(Content)<CaseStudyContentProps>`
  max-height: ${(props) => (props.isTest ? 'calc(100vh - 450px)' : 'calc(100vh - 220px)')};
  overflow: hidden;

  /* Mobile: sem altura fixa/scroll interno — o conteúdo (contexto, alternativas e
     navegação) deve renderizar por completo e a página rola normalmente. */
  @media screen and (max-width: 900px) {
    max-height: none;
    overflow: visible;
  }
`;

export const SplitLayout = styled.div<{ $isTest?: boolean }>`
  display: flex;
  flex-direction: row;
  width: 100%;
  flex: 1;
  min-height: 0;
  overflow: hidden;

  @media screen and (max-width: 900px) {
    flex-direction: column;
    overflow: visible;
  }
`;

export const ContextColumn = styled.div`
  flex: 3;
  padding: 20px 24px 24px;
  overflow-y: auto;
  /* Limita a altura para que o scroll ative quando o conteúdo extrapolar */
  max-height: calc(100vh - 340px);
  border-right: 1px solid ${colors.lightGray};

  font-family: Manrope, sans-serif;
  font-size: 15px;
  line-height: 1.7;
  color: ${colors.black};

  h3,
  h4 {
    margin: 0 0 8px;
    font-weight: 700;
    font-size: 14px;
    text-transform: uppercase;
  }

  p {
    margin: 0 0 12px;
  }

  ul,
  ol {
    margin: 12px 0;
    padding-left: 20px;
  }

  li {
    margin-bottom: 6px;
  }

  table {
    border-collapse: collapse;
    width: 100%;
    margin: 12px 0;
    font-size: 13px;
  }

  th,
  td {
    border: 1px solid #ccc;
    padding: 6px 10px;
  }

  th {
    background: ${colors.grayThree};
    font-weight: 600;
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
    margin: 10px 0;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${colors.borderGray};
    border-radius: 4px;
  }

  @media screen and (max-width: 900px) {
    border-right: none;
    border-bottom: 1px solid ${colors.lightGray};
    max-height: none;
    overflow-y: visible;
  }
`;

export const QuestionColumn = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media screen and (max-width: 900px) {
    overflow: visible;
  }
`;

export const QuestionScrollArea = styled.div`
  flex: 1;
  padding: 20px 24px 16px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }

  @media screen and (max-width: 900px) {
    padding: 16px 10px 10px;
    overflow-y: visible;
  }
`;

export const StepCode = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin: 0 0 8px;

  img {
    width: 16px;
    height: 16px;
  }

  p {
    font-family: Manrope;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: ${colors.gray};
    margin: 0;
  }
`;

export const StepFooter = styled.div`
  width: 100%;
  border-top: 1px solid #efefef;
  padding: 16px 24px 0 16px;
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;

  @media screen and (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

export const FooterCenter = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media screen and (max-width: 768px) {
    width: 100%;
    flex-wrap: wrap;
    justify-content: center;
  }
`;


type NavButtonProps = {
  disabled?: boolean;
  $variant?: 'default' | 'primary';
};

export const NavButton = styled.button<NavButtonProps>`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 20px;
  border-radius: 8px;
  font-family: Manrope, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props) => (props.disabled ? 0.4 : 1)};
  transition: all 0.2s;
  background: ${(props) => (props.$variant === 'primary' ? colors.primary : colors.white)};
  border: ${(props) =>
    props.$variant === 'primary' ? 'none' : `1px solid ${colors.borderGray}`};
  color: ${(props) => (props.$variant === 'primary' ? colors.white : colors.black)};

  &:hover:not(:disabled) {
    background: ${(props) =>
      props.$variant === 'primary' ? colors.primaryDark : colors.white};
    border-color: ${(props) =>
      props.$variant === 'primary' ? 'transparent' : colors.primary};
    color: ${(props) => (props.$variant === 'primary' ? colors.white : colors.primary)};
  }

  @media screen and (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

export const StepIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: Manrope, sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: ${colors.grayTwo};

  select {
    border: 1px solid ${colors.borderGray};
    border-radius: 6px;
    padding: 4px 8px;
    font-family: Manrope, sans-serif;
    font-size: 14px;
    cursor: pointer;
    background: ${colors.white};
    color: ${colors.black};
    outline: none;

    &:focus {
      border-color: ${colors.primary};
    }
  }
`;

export const ConfirmButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  min-width: 200px;
  padding: 0 20px;
  border-radius: 8px;
  border: none;
  background: ${colors.primary};
  color: ${colors.white};
  font-family: Manrope, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${colors.primaryDark};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px ${colors.primary}40;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media screen and (max-width: 768px) {
    width: 100%;
  }
`;
